import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';

import {
  Card, BlockStack, InlineStack, Text, Button, Select, TextField,
  Checkbox, Banner,
} from '@shopify/polaris';
import { CheckIcon, ImportIcon, ExportIcon } from '@shopify/polaris-icons';

import { packingRulesIndex, packingRuleCreate, packingRuleUpdate, packingRuleFileUpload } from '../../redux/actions/packingRules';
import { collectionsIndex } from '../../redux/actions/collections';
import { tagsIndex } from '../../redux/actions/tags';
import { PACKING_RULE_TYPE_LABELS as RULE_TYPE_LABELS, PACKING_RULE_LEVEL_LABELS as LEVEL_LABELS } from '../../constants';
import ProductSearchPanel from './ProductSearchPanel';

const RULE_TYPE_OPTIONS = Object.keys(RULE_TYPE_LABELS).map(value => ({ label: RULE_TYPE_LABELS[value], value: value }));
// Product and variant share one choice - which level the rule gets is
// decided by whether the admin picks a product row or one of its
// variants in the catalog search
const LEVEL_OPTIONS = [
  { label: 'Product or variant', value: 'catalog' },
  { label: LEVEL_LABELS['collection'], value: 'collection' },
  { label: LEVEL_LABELS['tag'], value: 'tag' },
  { label: LEVEL_LABELS['order'], value: 'order' }
];

const RuleForm = ({ packingRules, collections, tags, loadPackingRules, loadCollections, loadTags, createPackingRule, updatePackingRule, uploadRuleFile }) => {
  let navigate = useNavigate();
  let { id } = useParams();
  const editing = Boolean(id);

  const [submitted, setSubmitted] = useState(false);
  const [level, setLevel] = useState('catalog');
  const [ruleType, setRuleType] = useState('generic');
  const [name, setName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [shipmentLevel, setShipmentLevel] = useState(false);
  // The catalog item this rule targets: { productId, variantId?,
  // productName, variantOptions? } - picked via catalog search so admins
  // never have to look up raw Shopify ids
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [collectionId, setCollectionId] = useState('');
  const [tag, setTag] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  const existingRule = packingRules.packingRules.find(rule => rule.id === id);

  useEffect(() => {
    if (editing && packingRules.packingRules.length === 0) {
      loadPackingRules();
    }
  }, [editing, loadPackingRules, packingRules.packingRules.length]);

  // Grouping targets are chosen from the store's collections and tags
  useEffect(() => {
    if (level === 'collection' && collections.collections.length === 0) {
      loadCollections();
    }
    if (level === 'tag' && tags.tags.length === 0) {
      loadTags();
    }
  }, [level, collections.collections.length, tags.tags.length, loadCollections, loadTags]);

  useEffect(() => {
    if (editing && existingRule) {
      // Stored levels are product/variant; the picker is a combined
      // catalog search so they both map back to 'catalog' here
      setLevel(existingRule.level === 'variant' || existingRule.level === 'product' ? 'catalog' : existingRule.level);
      setRuleType(existingRule.rule_type);
      setName(existingRule.name || '');
      setRuleDescription(existingRule.description || '');
      setShipmentLevel(existingRule.shipment_level ? true : false);

      if (existingRule.level === 'variant') {
        let [p, v] = (existingRule.target_id || '').split('#');
        setSelectedTarget({
          productId: p || '',
          variantId: v || '',
          productName: existingRule.product_name,
          variantOptions: existingRule.variant_options
        });
      } else if (existingRule.level === 'product') {
        setSelectedTarget({
          productId: existingRule.target_id || '',
          productName: existingRule.product_name
        });
      } else if (existingRule.level === 'collection') {
        setCollectionId(existingRule.target_id || '');
      } else if (existingRule.level === 'tag') {
        setTag(existingRule.target_id || '');
      }
    }
  }, [existingRule, editing]);

  useEffect(() => {
    if (!editing && packingRules.newPackingRule) { navigate('/rules') }
    if (editing && packingRules.updatedPackingRule) { navigate('/rules') }
  }, [packingRules.newPackingRule, packingRules.updatedPackingRule, editing, navigate]);

  // Picking a variant row means the rule is variant-level; picking the
  // product row means product-level
  const ruleLevel = level === 'catalog'
    ? (selectedTarget?.variantId ? 'variant' : 'product')
    : level;

  // Critical shipping requirements are always whole-shipment rules -
  // getting one wrong can ruin the shipment, so they can't live on an
  // item checklist.
  // Order-level rules are shipment-level by definition.
  const shipmentLevelForced = ruleType === 'critical_shipping_requirement' || level === 'order';
  const effectiveShipmentLevel = shipmentLevelForced ? true : shipmentLevel;

  const targetId = () => {
    switch(level) {
      case 'catalog':
        return selectedTarget?.productId && selectedTarget?.variantId
          ? `${selectedTarget.productId}#${selectedTarget.variantId}`
          : selectedTarget?.productId?.toString() || '';
      case 'collection':
        return collectionId;
      case 'tag':
        return tag;
      default:
        return null;
    }
  }

  const formValid = () => {
    if (!name || !ruleDescription) { return false; }
    if (level !== 'order' && !targetId()) { return false; }
    return true;
  }

  const submitRule = () => {
    setSubmitted(true);

    let rule = {
      level: ruleLevel,
      target_id: level === 'order' ? null : targetId(),
      rule_type: ruleType,
      name: name,
      description: ruleDescription,
      shipment_level: effectiveShipmentLevel
    };

    // Catalog display fields - the rules list and this form show the
    // product name/variant options instead of raw ids. [] clears stale
    // options when a rule's level changed from variant to product.
    if (level === 'catalog') {
      rule.product_name = selectedTarget?.productName || null;
      rule.variant_options = selectedTarget?.variantId ? (selectedTarget?.variantOptions || []) : [];
    }

    // Grouping display name, so the rules list reads "Summer Collection"
    // instead of a bare id. Falls back to the stored name when the
    // collections/tags list isn't loaded (e.g. editing without reopening
    // the dropdown).
    if (level === 'collection') {
      let chosen = collections.collections.find(collection => String(collection.id) === String(collectionId));
      rule.grouping_name = chosen?.name || existingRule?.grouping_name || null;
    }

    if (level === 'tag') {
      rule.grouping_name = tag || existingRule?.grouping_name || null;
    }

    if (editing) {
      updatePackingRule({ id: id, ...rule });
    } else {
      createPackingRule(rule);
    }
  }

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      let chosen = e.target.files[0];

      if (!chosen.name.toLowerCase().endsWith('.pdf')) {
        setFileError('Print item files must be PDFs.');
        setFile(null);
        return;
      }

      setFileError(null);
      setFile(chosen);
    }
  }

  const handleFileUpload = (e) => {
    e.preventDefault();
    if (file) {
      uploadRuleFile(id, file);
      setFile(null);
    }
  }

  const targetFields = () => {
    if (level === 'order') {
      return <Text as="p">This rule applies to every shipment - no target needed.</Text>;
    }

    if (level === 'catalog') {
      // Search results can run long, so the picker lives in its own
      // card below the form instead of pushing the rest of the fields
      // out of view
      return (
        <Banner>
          <p>Select the product or variant this rule targets using the catalog search in the card below.</p>
        </Banner>
      );
    }

    if (level === 'collection') {
      return (
        <BlockStack gap="200">
          <Text as="p">Select the collection this rule targets.</Text>
          { collections.isFetching &&
            <LoadingSpinner size="small" statusText="Loading collections" />
          }
          { !collections.isFetching &&
            <Select
              requiredIndicator
              label="Collection"
              placeholder="Choose a collection"
              options={collections.collections
                .filter(collection => collection.id != null)
                .map(collection => ({ label: collection.name, value: String(collection.id) }))}
              value={String(collectionId)}
              onChange={setCollectionId}
            />
          }
          { collections.errors &&
            <Banner tone="critical"><p>{collections.errors}</p></Banner>
          }
        </BlockStack>
      );
    }

    return (
      <BlockStack gap="200">
        <Text as="p">Select the product tag this rule targets.</Text>
        { tags.isFetching &&
          <LoadingSpinner size="small" statusText="Loading tags" />
        }
        { !tags.isFetching &&
          <Select
            requiredIndicator
            label="Tag"
            placeholder="Choose a tag"
            options={tags.tags.map(tagName => ({ label: tagName, value: tagName }))}
            value={tag}
            onChange={setTag}
          />
        }
        { tags.errors &&
          <Banner tone="critical"><p>{tags.errors}</p></Banner>
        }
      </BlockStack>
    );
  }

  return(
    <SettingsPage
      title={editing ? 'Edit Packing Rule' : 'New Packing Rule'}
      description="Have questions or need some help? Contact us at support@athousandapps.com"
    >
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">{editing ? 'Edit Packing Rule' : 'New Packing Rule'}</Text>

          { (editing && packingRules.packingRules.length === 0) &&
            <LoadingSpinner size="small" statusText="Loading packing rule"/>
          }

          { (!editing || existingRule) &&
            <>
              <Select
                label="Rule type"
                options={RULE_TYPE_OPTIONS}
                value={ruleType}
                onChange={setRuleType}
                requiredIndicator
              />

              <Select
                label="Applies to"
                options={LEVEL_OPTIONS}
                value={level}
                onChange={(value) => { setLevel(value); setSelectedTarget(null); }}
                requiredIndicator
              />

              {targetFields()}

              <TextField
                requiredIndicator
                label="Name"
                placeholder="Short checklist label, e.g. Add fragile stickers"
                value={name}
                onChange={setName}
                autoComplete="off"
              />

              <TextField
                requiredIndicator
                label="Description"
                placeholder="What the packer needs to do or check"
                multiline={3}
                value={ruleDescription}
                onChange={setRuleDescription}
                autoComplete="off"
              />

              { level !== 'order' &&
                <Checkbox
                  checked={effectiveShipmentLevel}
                  disabled={ruleType === 'critical_shipping_requirement'}
                  onChange={setShipmentLevel}
                  label={ruleType === 'critical_shipping_requirement'
                    ? 'Applies to the whole shipment (critical shipping requirements always do)'
                    : 'Applies to the whole shipment instead of the item'}
                />
              }

              <InlineStack align="start">
                <Button
                  variant="primary"
                  icon={CheckIcon}
                  disabled={submitted || !formValid()}
                  onClick={() => submitRule()}
                  loading={submitted || packingRules.isCreating || packingRules.isUpdating}
                >
                  {editing ? 'Save Packing Rule' : 'Create Packing Rule'}
                </Button>
              </InlineStack>
            </>
          }

          { packingRules.errors &&
            <Banner tone="critical"><p>{packingRules.errors}</p></Banner>
          }
        </BlockStack>
      </Card>

      { level === 'catalog' &&
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Catalog Search</Text>
            <Text as="p">Search the catalog and select the product or variant this rule targets.</Text>
            <ProductSearchPanel
              selected={selectedTarget}
              onSelect={(target) => setSelectedTarget(target)}
              onClear={() => setSelectedTarget(null)}
            />
          </BlockStack>
        </Card>
      }

      { editing && ruleType === 'add_print_item' &&
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Print Item File</Text>
            <Text as="p">This PDF is linked in the warehouse app so the packer can print and include it.</Text>

            { existingRule?.file_url &&
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Current file:</Text>
                <InlineStack align="start">
                  <a href={existingRule.file_url} target="_blank" rel="noreferrer">
                    <Button icon={ExportIcon}>
                      Download {existingRule.file_key?.split('/').pop()}
                    </Button>
                  </a>
                </InlineStack>
              </BlockStack>
            }
            { !existingRule?.file_url &&
              <Banner tone="warning"><p>No file uploaded yet.</p></Banner>
            }

            <Text as="h3" variant="headingSm">Upload a PDF:</Text>
            <form onChange={(e) => handleFileChange(e)} onSubmit={(e) => handleFileUpload(e)}>
              <label>
                PDF file
                <input type="file" name="file" accept="application/pdf" />
              </label>
              <br/>
              <br/>
              <Button icon={ImportIcon} disabled={!file || packingRules.isUploadingFile} loading={packingRules.isUploadingFile} submit>
                Upload File
              </Button>
            </form>
            { fileError &&
              <Banner tone="critical"><p>{fileError}</p></Banner>
            }
          </BlockStack>
        </Card>
      }

      { !editing && ruleType === 'add_print_item' &&
        <Banner>
          <p>You can upload the print file after the rule is created, from the edit screen.</p>
        </Banner>
      }
    </SettingsPage>
  )
}

const mapStateToProps = state => ({
  packingRules: state.packingRules,
  collections: state.collections,
  tags: state.tags,
});

const mapDispatchToProps = (dispatch) => ({
  loadPackingRules: () => dispatch(packingRulesIndex()),
  loadCollections: () => dispatch(collectionsIndex()),
  loadTags: () => dispatch(tagsIndex()),
  createPackingRule: (rule) => dispatch(packingRuleCreate(rule)),
  updatePackingRule: (rule) => dispatch(packingRuleUpdate(rule)),
  uploadRuleFile: (ruleId, file) => dispatch(packingRuleFileUpload(ruleId, file)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RuleForm);
